import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Cookie, ShieldCheck, ChartLine, MegaphoneSimple, X, Gear } from "@phosphor-icons/react";
import {
  getConsent, hasConsentDecision, saveConsent, acceptAll, rejectAll,
  OPEN_BANNER_EVENT,
} from "../lib/consent";

/**
 * GDPR / PECR-compliant cookie banner.
 * - Renders as a bottom sheet on first visit
 * - "Accept all" and "Reject all" have equal visual weight (ICO requirement)
 * - "Manage preferences" opens a modal with granular toggles
 * - Reopens itself via the OPEN_BANNER_EVENT (fired by footer link)
 */
export default function CookieBanner() {
  const [open, setOpen] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [prefs, setPrefs] = useState({ essential: true, analytics: false, marketing: false });

  // Show on first visit; hydrate prefs from storage when re-opened
  useEffect(() => {
    if (!hasConsentDecision()) setOpen(true);
    const existing = getConsent();
    if (existing) setPrefs(existing);
    const onOpen = () => {
      const cur = getConsent() || { essential: true, analytics: false, marketing: false };
      setPrefs(cur);
      setShowManage(true);
      setOpen(true);
    };
    window.addEventListener(OPEN_BANNER_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_BANNER_EVENT, onOpen);
  }, []);

  const doAccept = () => { acceptAll(); setOpen(false); setShowManage(false); };
  const doReject = () => { rejectAll(); setOpen(false); setShowManage(false); };
  const doSave = () => { saveConsent(prefs); setOpen(false); setShowManage(false); };
  const toggle = (key) => (e) => setPrefs((p) => ({ ...p, [key]: e.target.checked }));

  if (!open) return null;

  return (
    <>
      {/* Backdrop for the manage modal only */}
      {showManage && (
        <div
          className="fixed inset-0 z-[60]"
          style={{ background: "rgba(18,28,24,0.55)" }}
          onClick={() => setShowManage(false)}
          data-testid="cookie-manage-backdrop"
        />
      )}

      {/* Bottom banner */}
      {!showManage && (
        <div
          className="fixed inset-x-0 bottom-0 z-[55] px-3 sm:px-5 pb-3 sm:pb-5"
          data-testid="cookie-banner"
        >
          <div
            className="max-w-4xl mx-auto rounded-2xl shadow-2xl border p-5 sm:p-6 bg-white"
            style={{ borderColor: "var(--border-stone)", boxShadow: "0 12px 40px rgba(17,82,59,0.18)" }}
          >
            <div className="flex items-start gap-4">
              <span className="flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center" style={{ background: "rgba(226,109,41,0.14)" }}>
                <Cookie size={22} weight="fill" style={{ color: "var(--brand-orange)" }} />
              </span>
              <div className="flex-1 min-w-0">
                <h2 className="font-display font-extrabold text-base sm:text-lg" style={{ color: "var(--brand-green)" }}>
                  Cookies help us improve MyHandyHub
                </h2>
                <p className="text-sm text-[var(--text-muted)] mt-1 leading-relaxed">
                  We use essential cookies to make the site work. With your permission, we also use
                  analytics cookies to understand how it's used and marketing cookies to measure our ads.
                  You can change your mind anytime in <em>Cookie preferences</em>.
                  {" "}
                  <Link to="/privacy" className="underline font-semibold" style={{ color: "var(--brand-green)" }} data-testid="cookie-banner-privacy-link">
                    Read our Privacy policy
                  </Link>.
                </p>

                <div className="mt-4 flex flex-wrap gap-2">
                  {/* Reject All must be equally prominent to Accept All under ICO guidance */}
                  <button
                    onClick={doReject}
                    className="px-5 py-2.5 rounded-full font-bold text-sm border-2 hover:bg-[#FAF8F2] transition-colors"
                    style={{ borderColor: "var(--brand-green)", color: "var(--brand-green)" }}
                    data-testid="cookie-reject-all"
                  >
                    Reject all
                  </button>
                  <button
                    onClick={doAccept}
                    className="px-5 py-2.5 rounded-full font-bold text-sm text-white transition-transform hover:scale-[1.02]"
                    style={{ background: "var(--brand-green)" }}
                    data-testid="cookie-accept-all"
                  >
                    Accept all
                  </button>
                  <button
                    onClick={() => setShowManage(true)}
                    className="px-4 py-2.5 rounded-full font-semibold text-sm inline-flex items-center gap-1.5 hover:bg-[#FAF8F2] transition-colors"
                    style={{ color: "var(--brand-green)" }}
                    data-testid="cookie-manage"
                  >
                    <Gear size={14} weight="bold" /> Manage preferences
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manage preferences modal */}
      {showManage && (
        <div
          className="fixed inset-0 z-[65] flex items-center justify-center p-4"
          data-testid="cookie-manage-modal"
        >
          <div
            className="brand-card p-6 sm:p-7 max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-display font-extrabold text-xl" style={{ color: "var(--brand-green)" }}>
                  Cookie preferences
                </h3>
                <p className="text-sm text-[var(--text-muted)] mt-1">
                  Choose which cookies you're happy for us to use.
                </p>
              </div>
              <button
                onClick={() => setShowManage(false)}
                aria-label="Close"
                className="p-1 rounded-full hover:bg-[#FAF8F2]"
                data-testid="cookie-manage-close"
              >
                <X size={20} />
              </button>
            </div>

            <ul className="mt-5 space-y-3">
              <CategoryRow
                icon={ShieldCheck}
                title="Strictly necessary"
                desc="Required for the site to work — login, security, remembering that you've made a cookie choice. These can't be turned off."
                locked
                checked={true}
                testId="cookie-cat-essential"
              />
              <CategoryRow
                icon={ChartLine}
                title="Analytics"
                desc="Helps us count visits and understand which pages work well. Powers Google Analytics 4, PostHog and our internal traffic reports."
                checked={prefs.analytics}
                onChange={toggle("analytics")}
                testId="cookie-cat-analytics"
              />
              <CategoryRow
                icon={MegaphoneSimple}
                title="Marketing"
                desc="Measures the effectiveness of our ads and lets us show you relevant offers. Off by default."
                checked={prefs.marketing}
                onChange={toggle("marketing")}
                testId="cookie-cat-marketing"
              />
            </ul>

            <div className="mt-5 flex flex-wrap gap-2 justify-end">
              <button onClick={doReject} className="px-4 py-2 rounded-full font-semibold text-sm border-2 hover:bg-[#FAF8F2]" style={{ borderColor: "var(--brand-green)", color: "var(--brand-green)" }} data-testid="cookie-manage-reject">
                Reject all
              </button>
              <button onClick={doSave} className="px-5 py-2 rounded-full font-bold text-sm text-white" style={{ background: "var(--brand-green)" }} data-testid="cookie-manage-save">
                Save my choices
              </button>
            </div>

            <p className="text-xs text-[var(--text-muted)] mt-4 leading-relaxed">
              You can change these settings anytime from the <em>Cookie preferences</em> link in the footer.
              See our <Link to="/privacy" className="underline">Privacy policy</Link> for details.
            </p>
          </div>
        </div>
      )}
    </>
  );
}

function CategoryRow({ icon: Icon, title, desc, checked, onChange, locked, testId }) {
  return (
    <li className="rounded-xl p-4 border flex items-start gap-3" style={{ borderColor: "var(--border-stone)", background: "#FAF8F2" }}>
      <span className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "rgba(17,82,59,0.10)" }}>
        <Icon size={18} weight="duotone" style={{ color: "var(--brand-green)" }} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-3">
          <div className="font-display font-extrabold text-sm" style={{ color: "var(--brand-green)" }}>{title}</div>
          {locked ? (
            <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full" style={{ background: "rgba(17,82,59,0.10)", color: "var(--brand-green)" }}>Always on</span>
          ) : (
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={checked}
                onChange={onChange}
                data-testid={testId}
              />
              <div className="w-11 h-6 bg-stone-300 rounded-full peer peer-checked:bg-[var(--brand-green)] transition-colors" />
              <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full transition-transform peer-checked:translate-x-5 shadow" />
            </label>
          )}
        </div>
        <p className="text-xs text-[var(--text-muted)] mt-1 leading-relaxed">{desc}</p>
      </div>
    </li>
  );
}
